import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Download, CheckCircle2, Clock, XCircle, FileCheck, Wallet, Pencil, AlertTriangle, Plus, Trash2, BadgeIndianRupee } from "lucide-react";
import SkuCombobox from "@/components/SkuCombobox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: "submitted", label: "Submitted", icon: Clock },
  { key: "approved", label: "Approved by Admin", icon: CheckCircle2 },
  { key: "invoiced", label: "Tax Invoice Ready", icon: FileCheck },
  { key: "paid", label: "Payment Received", icon: BadgeIndianRupee },
];

function StatusTimeline({ data }) {
  const status = data.status;
  // Anchor step-completion on real lifecycle checkpoints (timestamps + flags)
  // instead of exact status-string matches. Post-approval statuses like
  // "Info Requested" or "Edit Requested" used to fall into the `else 0`
  // branch and made the "Approved by Admin" step regress — so auto-approved
  // IBO bills briefly appeared "not approved". Now we look at approved_at.
  const rejected = status === "Rejected";
  const isApproved = !rejected && (
    !!data.approved_at
    || status === "Approved"
    || status === "Invoice Uploaded"
    || status === "Info Requested"
    || status === "Edit Requested"
  );
  const isInvoiced = !rejected && (data.invoice_available || !!data.tax_invoice_uploaded_at);
  const isPaid = !rejected && (data.payment_status === "Received" || !!data.payment_received_at);
  const activeIdx = isPaid ? 3 : isInvoiced ? 2 : isApproved ? 1 : 0;
  return (
    <div className="flex items-center justify-between gap-2 relative py-4">
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const done = i <= activeIdx && !rejected;
        return (
          <React.Fragment key={s.key}>
            <div className="flex flex-col items-center text-center flex-1">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${done ? "bg-primary border-primary text-primary-foreground" : "bg-background border-muted text-muted-foreground"}`} data-testid={`b2b-track-step-${s.key}-${done ? "done" : "todo"}`}>
                <Icon className="w-5 h-5" />
              </div>
              <span className={`text-xs mt-2 ${done ? "font-medium" : "text-muted-foreground"}`}>{s.label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 ${i < activeIdx && !rejected ? "bg-primary" : "bg-border"}`} />
            )}
          </React.Fragment>
        );
      })}
      {rejected && (
        <div className="absolute -top-1 right-0 flex items-center gap-1 text-red-600 text-xs font-medium">
          <XCircle className="w-4 h-4" /> Rejected
        </div>
      )}
    </div>
  );
}

export default function B2BTrack() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Accept the reference from EITHER router-state (in-app navigate) OR the
  // `?ref=` query string (clicking a Track link that opens in a new tab).
  const initialRef = location.state?.ref || searchParams.get("ref") || "";
  const [ref, setRef] = useState(initialRef);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editReason, setEditReason] = useState("");
  const [editing, setEditing] = useState(false);

  const [partyEditOpen, setPartyEditOpen] = useState(false);
  const [partyEditForm, setPartyEditForm] = useState(null);
  const [partyEditSaving, setPartyEditSaving] = useState(false);
  const [skuOptions, setSkuOptions] = useState([]);

  const lookup = useCallback(async (referenceNumber) => {
    const r = (referenceNumber || "").trim();
    if (!r) return;
    setLoading(true);
    setData(null);
    try {
      const { data } = await axios.get(`${API}/b2b/track`, { params: { reference_number: r } });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Not found");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (initialRef) lookup(initialRef);
  }, [initialRef, lookup]);

  const download = () => {
    window.open(`${API}/b2b/invoice/${encodeURIComponent(data.reference_number)}`, "_blank");
  };

  const submitEditRequest = async () => {
    if (editReason.trim().length < 3) { toast.error("Please describe what needs to change"); return; }
    setEditing(true);
    try {
      await axios.post(`${API}/b2b/request-edit/${encodeURIComponent(data.reference_number)}`, { edit_reason: editReason.trim() });
      toast.success("Edit request submitted");
      setEditOpen(false);
      setEditReason("");
      lookup(data.reference_number);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit edit request");
    } finally { setEditing(false); }
  };

  const openPartyEdit = async () => {
    try {
      const { data: skus } = await axios.get(`${API}/b2b/skus`);
      setSkuOptions(skus || []);
    } catch (_) { setSkuOptions([]); }
    setPartyEditForm({
      party_name: data.party_name || "",
      party_gst: data.party_gst || "",
      party_billing_address: data.party_billing_address || "",
      party_shipping_address: data.party_shipping_address || "",
      party_phone: data.party_phone || "",
      notes: data.notes || "",
      items: (data.items || []).map((it) => ({ sku_id: it.sku_id, sku_name: it.sku_name, quantity: it.quantity })),
    });
    setPartyEditOpen(true);
  };

  const updateItem = (idx, patch) => {
    setPartyEditForm((f) => {
      const items = [...f.items];
      items[idx] = { ...items[idx], ...patch };
      if (patch.sku_id) {
        const s = skuOptions.find((x) => x.id === patch.sku_id);
        if (s) items[idx].sku_name = s.sku_name;
      }
      return { ...f, items };
    });
  };
  const addItem = () => setPartyEditForm((f) => ({ ...f, items: [...f.items, { sku_id: "", sku_name: "", quantity: 1 }] }));
  const removeItem = (idx) => setPartyEditForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const savePartyEdit = async () => {
    const f = partyEditForm;
    if (!f.party_name.trim() || !f.party_billing_address.trim() || !f.party_shipping_address.trim() || !f.party_phone.trim()) {
      toast.error("Party name, billing/shipping address and phone are required");
      return;
    }
    const items = (f.items || []).filter((i) => i.sku_id && Number(i.quantity) > 0);
    if (items.length === 0) { toast.error("Add at least one product"); return; }
    setPartyEditSaving(true);
    try {
      await axios.post(`${API}/b2b/edit-submission/${encodeURIComponent(data.reference_number)}`, {
        party_name: f.party_name.trim(),
        party_gst: f.party_gst.trim(),
        party_billing_address: f.party_billing_address.trim(),
        party_shipping_address: f.party_shipping_address.trim(),
        party_phone: f.party_phone.trim(),
        notes: f.notes.trim(),
        items: items.map((i) => ({ sku_id: i.sku_id, quantity: Number(i.quantity) })),
      });
      toast.success("Submission updated — awaiting admin re-review");
      setPartyEditOpen(false);
      lookup(data.reference_number);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update submission");
    } finally { setPartyEditSaving(false); }
  };

  return (
    <div className="min-h-screen bg-muted/30 p-4 md:p-8">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/login")} data-testid="b2b-track-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Login
          </Button>
        </div>
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Track B2B Submission</h1>
          <p className="text-sm text-muted-foreground mt-1">Enter the reference number you received when submitting.</p>
        </div>

        <Card className="p-6">
          <form onSubmit={(e) => { e.preventDefault(); lookup(ref); }} className="flex items-end gap-3">
            <div className="flex-1 space-y-2">
              <Label>Reference Number</Label>
              <Input required placeholder="e.g. REF260700001" value={ref} onChange={(e) => setRef(e.target.value)} data-testid="b2b-track-input" />
            </div>
            <Button type="submit" disabled={loading} data-testid="b2b-track-search-btn">
              {loading ? "Looking up..." : "Track"}
            </Button>
          </form>
        </Card>

        {data && (
          <Card className="p-6 space-y-6" data-testid="b2b-track-result">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Reference</div>
                <div className="font-mono font-bold text-xl">{data.reference_number}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Party</div>
                <div className="font-medium">{data.party_name}</div>
              </div>
            </div>

            <StatusTimeline data={data} />

            {data.admin_remarks && (
              <div className="bg-muted/40 border border-border rounded-md p-3 text-sm">
                <div className="text-xs text-muted-foreground mb-1">Admin remarks</div>
                {data.admin_remarks}
              </div>
            )}

            {data.status === "Info Requested" && data.accountant_remark && (
              <div className="bg-orange-50 border border-orange-300 rounded-md p-4 space-y-3" data-testid="b2b-accountant-remark-banner">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-orange-900">Accountant needs your input</div>
                    {data.accountant_remark_at && (
                      <div className="text-xs text-orange-700">{new Date(data.accountant_remark_at).toLocaleString()}</div>
                    )}
                    <div className="text-sm mt-2 whitespace-pre-wrap text-orange-900">{data.accountant_remark}</div>
                  </div>
                </div>
                <Button size="sm" onClick={openPartyEdit} className="w-full" data-testid="b2b-edit-submission-btn">
                  <Pencil className="w-4 h-4 mr-2" /> Edit Submission
                </Button>
              </div>
            )}

            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Products</div>
              <div className="space-y-1">
                {data.items.map((it) => (
                  <div key={it.sku_id} className="flex items-center justify-between text-sm border-b border-border py-1.5">
                    <span>{it.sku_name}</span>
                    <span className="font-medium">× {it.quantity}</span>
                  </div>
                ))}
              </div>
            </div>

            {data.notes && (
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Your Notes</div>
                <div className="text-sm bg-muted/40 border border-border rounded-md p-3 whitespace-pre-wrap">{data.notes}</div>
              </div>
            )}

            {data.status === "Edit Requested" && data.edit_reason && (
              <div className="bg-amber-50 border border-amber-300 rounded-md p-3 text-sm">
                <div className="text-xs text-amber-700 font-medium mb-1">Edit requested — awaiting revised invoice</div>
                <div className="whitespace-pre-wrap">{data.edit_reason}</div>
              </div>
            )}

            {data.invoice_available && (
              <div className="pt-2 space-y-2">
                <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                  {data.invoice_type && (
                    <span className={`px-2 py-0.5 rounded-full font-medium border ${data.invoice_type === "Proforma" ? "bg-purple-50 border-purple-300 text-purple-800" : "bg-blue-50 border-blue-300 text-blue-800"}`}>
                      {data.invoice_type} Invoice
                    </span>
                  )}
                  {data.invoice_number && <span>Invoice #: <span className="font-mono text-foreground">{data.invoice_number}</span></span>}
                  {data.invoice_amount > 0 && <span>Amount: <span className="font-semibold text-foreground">₹{Number(data.invoice_amount).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></span>}
                  {data.invoice_version > 1 && <span className="text-amber-600">Revision {data.invoice_version}</span>}
                </div>
                <Button onClick={download} className="w-full" data-testid="b2b-download-invoice">
                  <Download className="w-4 h-4 mr-2" /> Download {data.invoice_type || "Tax"} Invoice (PDF)
                </Button>
                {data.status === "Invoice Uploaded" && (
                  <Button variant="outline" onClick={() => setEditOpen(true)} className="w-full" data-testid="b2b-request-edit-btn">
                    <Pencil className="w-4 h-4 mr-2" /> Request Edit
                  </Button>
                )}
              </div>
            )}

            <div className="border-t border-border pt-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Wallet className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Payment</span>
                </div>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full border ${data.payment_status === "Received" ? "bg-green-50 border-green-300 text-green-700" : data.payment_screenshot_uploaded ? "bg-amber-50 border-amber-300 text-amber-700" : "bg-muted border-border text-muted-foreground"}`}
                  data-testid="b2b-track-payment-status"
                >
                  {data.payment_status === "Received"
                    ? "Payment Received"
                    : data.payment_screenshot_uploaded
                      ? "Awaiting verification"
                      : "Not uploaded"}
                </span>
              </div>
              {data.payment_status !== "Received" && (
                <PaymentUploader reference={data.reference_number} alreadyUploaded={data.payment_screenshot_uploaded} onDone={() => lookup(data.reference_number)} />
              )}
            </div>
          </Card>
        )}

        <div className="text-center text-sm text-muted-foreground">
          Need to make a new submission?{" "}
          <Link to="/b2b/new" className="text-primary underline">Start here →</Link>
        </div>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Edit to Tax Invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-sm">What needs to be changed?</Label>
            <Textarea
              rows={4}
              value={editReason}
              onChange={(e) => setEditReason(e.target.value)}
              placeholder="e.g. GST number is wrong / Party name spelling / etc."
              data-testid="b2b-edit-reason"
            />
            <p className="text-xs text-muted-foreground">The accountant will re-issue an updated invoice.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={submitEditRequest} disabled={editing} data-testid="b2b-edit-submit">
              {editing ? "Submitting..." : "Submit Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={partyEditOpen} onOpenChange={setPartyEditOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Submission</DialogTitle>
          </DialogHeader>
          {partyEditForm && (
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground">
                Update the details as per the accountant&apos;s remark. Your submission will be sent back for admin re-review.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Party Name *</Label>
                  <Input value={partyEditForm.party_name} onChange={(e) => setPartyEditForm({ ...partyEditForm, party_name: e.target.value })} data-testid="b2b-edit-party-name" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Party Phone *</Label>
                  <Input value={partyEditForm.party_phone} onChange={(e) => setPartyEditForm({ ...partyEditForm, party_phone: e.target.value })} data-testid="b2b-edit-party-phone" />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <Label className="text-xs">Party GST</Label>
                  <Input value={partyEditForm.party_gst} onChange={(e) => setPartyEditForm({ ...partyEditForm, party_gst: e.target.value })} data-testid="b2b-edit-party-gst" />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <Label className="text-xs">Billing Address *</Label>
                  <Textarea rows={2} value={partyEditForm.party_billing_address} onChange={(e) => setPartyEditForm({ ...partyEditForm, party_billing_address: e.target.value })} data-testid="b2b-edit-party-billing" />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <Label className="text-xs">Shipping Address *</Label>
                  <Textarea rows={2} value={partyEditForm.party_shipping_address} onChange={(e) => setPartyEditForm({ ...partyEditForm, party_shipping_address: e.target.value })} data-testid="b2b-edit-party-shipping" />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <Label className="text-xs">Notes</Label>
                  <Textarea rows={2} value={partyEditForm.notes} onChange={(e) => setPartyEditForm({ ...partyEditForm, notes: e.target.value })} data-testid="b2b-edit-notes" />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Products *</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="b2b-edit-add-item">
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
                <div className="space-y-2">
                  {partyEditForm.items.map((it, idx) => (
                    <div key={idx} className="flex items-center gap-2" data-testid={`b2b-edit-item-${idx}`}>
                      <div className="flex-1">
                        <SkuCombobox
                          skus={skuOptions}
                          value={it.sku_id}
                          onChange={(v) => updateItem(idx, { sku_id: v })}
                          placeholder="Select product..."
                          testid={`b2b-edit-item-sku-${idx}`}
                        />
                      </div>
                      <Input
                        type="number" min="1" step="1"
                        className="w-24"
                        value={it.quantity}
                        onChange={(e) => updateItem(idx, { quantity: e.target.value })}
                        data-testid={`b2b-edit-item-qty-${idx}`}
                      />
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(idx)} data-testid={`b2b-edit-item-remove-${idx}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPartyEditOpen(false)}>Cancel</Button>
            <Button onClick={savePartyEdit} disabled={partyEditSaving} data-testid="b2b-edit-save-btn">
              {partyEditSaving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PaymentUploader({ reference, alreadyUploaded, onDone }) {
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["pdf", "jpg", "jpeg", "png", "webp"].includes(ext)) {
      toast.error("Only PDF or JPG/PNG/WEBP");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await axios.post(`${API}/b2b/payment/${encodeURIComponent(reference)}`, fd);
      toast.success("Uploaded — accountant will verify");
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">
        {alreadyUploaded ? "Replace payment screenshot" : "Upload payment screenshot (PDF or image)"}
      </Label>
      <Input
        type="file"
        accept="application/pdf,image/*"
        onChange={onFile}
        disabled={uploading}
        data-testid="b2b-track-payment-upload"
      />
    </div>
  );
}
